/**
 * Financial-Grade Presentation Formatters.
 * Project: Market-Hub
 * Governance: Presentation Leaf-Node Quarantine Invariant (ASOP / Zero Float Invariant).
 * 
 * Rules:
 * 1. High-value coins (price >= 10, e.g. BTC, ETH, SOL, BNB, OKB): 2 decimals with US thousands commas.
 * 2. Low-value coins (price < 10, e.g. BGB, HYPE): 4 decimals.
 * 3. Never display unformatted 8-decimal strings (no meme coin 8 decimals).
 * 4. Technical indicators:
 *    - RSI: strictly 2 decimals (0.00 - 100.00).
 *    - MACD DIF / DEA: adaptive 2 decimals (with comma if >= 1000) or 4 decimals for sub-10 coins.
 *    - Bollinger Bandwidth / MA Dist: 2 decimals with '%' (and signed +/- for distance).
 *    - Bollinger %B: 2 decimals.
 */

const HIGH_VALUE_SYMBOLS = new Set([
  'BTCUSDT',
  'ETHUSDT',
  'SOLUSDT',
  'BNBUSDT',
  'OKBUSDT',
  'BTC',
  'ETH',
  'SOL',
  'BNB',
  'OKB',
]);

/**
 * Parses any incoming price/metric value safely into a finite number for presentation leaf rendering.
 */
function toValidNumber(rawVal: unknown): number | null {
  if (rawVal === null || rawVal === undefined || rawVal === '' || rawVal === '--') {
    return null;
  }
  if (typeof rawVal === 'number') {
    return Number.isFinite(rawVal) ? rawVal : null;
  }
  if (typeof rawVal === 'string') {
    const cleaned = rawVal.replace(/,/g, '').trim();
    if (cleaned === '' || cleaned === '--') return null;
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}
const MEME_SYMBOLS = new Set([
  'PEPEUSDT',
  'SHIBUSDT',
  'BONKUSDT',
  'FLOKIUSDT',
  'PEPE',
  'SHIB',
]);

/**
 * Formats a crypto asset price according to high-value vs low-value financial standards.
 * - High-value coins (price >= 10 or HIGH_VALUE_SYMBOLS, e.g. BTC, ETH, SOL, BNB, OKB): 2 decimals with US thousand separators.
 * - Small-value coins (price < 10, e.g. BGB, HYPE): strictly 4 decimals.
 * - Meme coins (price < 0.0001 or MEME_SYMBOLS): 8 decimals.
 */
export function formatFinancialPrice(rawVal: unknown, symbol?: string): string {
  const num = toValidNumber(rawVal);
  if (num === null) return '--';

  const symUpper = symbol ? symbol.toUpperCase() : '';
  const isMeme = MEME_SYMBOLS.has(symUpper) || (num > 0 && num < 0.0001);
  if (isMeme) {
    return num.toFixed(8);
  }

  const isExplicitSmall = symUpper === 'BGBUSDT' || symUpper === 'BGB';
  const isHighValueSymbol = HIGH_VALUE_SYMBOLS.has(symUpper);

  if (!isExplicitSmall && (num >= 10 || isHighValueSymbol)) {
    return num.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  // Small-value coins (< 10, e.g. BGB, HYPE): strictly 4 decimal places
  return num.toFixed(4);
}

/**
 * Formats MACD metrics (DIF, DEA, Histogram).
 * DIF/DEA share the same price dimension as the underlying coin.
 * - For BTC/ETH/SOL (or absolute values >= 10): 2 decimals with thousand separators.
 * - For small coins (< 10): 4 decimals.
 */
export function formatMacd(rawVal: unknown, symbol?: string, basePrice?: number): string {
  const num = toValidNumber(rawVal);
  if (num === null) return '--';

  const isHighValueSymbol = symbol ? HIGH_VALUE_SYMBOLS.has(symbol.toUpperCase()) : false;
  const isHighBase = (basePrice !== undefined && basePrice !== null && basePrice >= 10);
  const isHighMagnitude = Math.abs(num) >= 10;

  if (isHighValueSymbol || isHighBase || isHighMagnitude) {
    return num.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  return num.toFixed(4);
}

/**
 * Formats RSI oscillator metrics (RSI 14, RSI 6).
 * Standard range [0, 100], always formatted to 2 decimals.
 */
export function formatRsi(rawVal: unknown): string {
  const num = toValidNumber(rawVal);
  if (num === null) return '--';
  return num.toFixed(2);
}

/**
 * Formats percentages (e.g. Bollinger Bandwidth %, MA distance %).
 * Options:
 * - signed: boolean (e.g. +1.25% vs -0.84%)
 * - decimals: default 2
 */
export function formatPercentage(
  rawVal: unknown,
  options: { signed?: boolean; decimals?: number } = {}
): string {
  const num = toValidNumber(rawVal);
  if (num === null) return '--';

  const { signed = false, decimals = 2 } = options;
  const formatted = num.toFixed(decimals);

  if (signed) {
    if (num > 0) return `+${formatted}%`;
    return `${formatted}%`;
  }

  return `${formatted}%`;
}

/**
 * Formats dimensionless ratios (e.g. Bollinger %B, TVS ratio).
 * Always formatted to 2 decimals.
 */
export function formatRatio(rawVal: unknown, decimals: number = 2): string {
  const num = toValidNumber(rawVal);
  if (num === null) return '--';
  return num.toFixed(decimals);
}

/**
 * Formats bid-ask spread or price difference.
 */
export function formatSpread(rawAsk: unknown, rawBid: unknown): string {
  const ask = toValidNumber(rawAsk);
  const bid = toValidNumber(rawBid);
  if (ask === null || bid === null) return '--';

  const diff = ask - bid;
  if (Math.abs(diff) >= 0.01) {
    return diff.toFixed(2) + ' USDT';
  }
  return diff.toFixed(4) + ' USDT';
}

/**
 * Canonical price formatter alias conforming to Stage 022E contract.
 * Preserves formatFinancialPrice behavior and supports currency prefix/suffix.
 */
export function formatPrice(
  rawVal: unknown,
  options?: { symbol?: string; decimals?: number; currency?: string }
): string {
  const symbol = options?.symbol;
  const currency = options?.currency;
  const res = formatFinancialPrice(rawVal, symbol);
  if (res === '--') return '--';
  if (currency) {
    return `${res} ${currency}`;
  }
  return res;
}

/**
 * Canonical percentage formatter alias conforming to Stage 022E contract.
 */
export function formatPercent(
  rawVal: unknown,
  options?: { signed?: boolean; decimals?: number }
): string {
  return formatPercentage(rawVal, options);
}

/**
 * Formats financial volume or turnover with locale-aware thousands commas
 * and optional abbreviations (万, 亿 or K, M, B).
 */
export function formatVolume(
  rawVal: unknown,
  options?: { abbreviate?: 'zh' | 'en'; decimals?: number }
): string {
  const num = toValidNumber(rawVal);
  if (num === null) return '--';

  const abbreviate = options?.abbreviate;
  const decimals = options?.decimals ?? 2;

  if (abbreviate === 'zh') {
    const abs = Math.abs(num);
    if (abs >= 100000000) {
      return (num / 100000000).toFixed(decimals) + ' 亿';
    }
    if (abs >= 10000) {
      return (num / 10000).toFixed(decimals) + ' 万';
    }
  } else if (abbreviate === 'en') {
    const abs = Math.abs(num);
    if (abs >= 1000000000) {
      return (num / 1000000000).toFixed(decimals) + ' B';
    }
    if (abs >= 1000000) {
      return (num / 1000000).toFixed(decimals) + ' M';
    }
    if (abs >= 1000) {
      return (num / 1000).toFixed(decimals) + ' K';
    }
  }

  return num.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  });
}

/**
 * Formats telemetry / causal latency in seconds with leaf quarantine.
 */
export function formatLatency(rawVal: unknown): string {
  const num = toValidNumber(rawVal);
  if (num === null) return '--';
  return `${num.toFixed(1)}s`;
}

